/**
 * F007: Stage Transition Modal — Confirmation modal with readiness checks
 *
 * Opens when a user clicks a transition action button. Fetches readiness
 * checks (if the transition requires them) and displays results.
 * Red blockers disable the Proceed button. If no blockers, the user can
 * proceed with an optional reason.
 *
 * @module client/src/components/modals/stage-transition-modal
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TransitionTarget {
  targetStage: string;
  targetSubstage: string;
  label: string;
  requiresReadinessCheck: boolean;
}

interface ReadinessCheck {
  checkName: string;
  severity: "red" | "amber" | "info" | "green";
  passed: boolean;
  message: string;
  resolution?: string;
  value?: string | number;
}

interface ReadinessResponse {
  checks: ReadinessCheck[];
  canProceed: boolean;
  blockerCount: number;
  warningCount: number;
}

interface StageTransitionModalProps {
  dealId: string;
  transition: TransitionTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Severity icon mapping
// ---------------------------------------------------------------------------

function SeverityIcon({ severity }: { severity: string }) {
  switch (severity) {
    case "red":
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    case "amber":
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case "info":
      return <Info className="h-4 w-4 text-blue-500" />;
    case "green":
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    default:
      return <Info className="h-4 w-4 text-muted-foreground" />;
  }
}

function severityBadgeVariant(severity: string) {
  switch (severity) {
    case "red":
      return "destructive" as const;
    case "amber":
      return "secondary" as const;
    case "green":
      return "default" as const;
    default:
      return "secondary" as const;
  }
}

function severityBadgeClassName(severity: string) {
  switch (severity) {
    case "red":
      return "";
    case "amber":
      return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900 dark:text-amber-200";
    case "info":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200";
    case "green":
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200";
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StageTransitionModal({
  dealId,
  transition,
  open,
  onOpenChange,
}: StageTransitionModalProps) {
  const [reason, setReason] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setReason("");
    }
  }, [open]);

  // Fetch readiness checks when modal opens and transition requires it
  const {
    data: readiness,
    isLoading: readinessLoading,
    error: readinessError,
  } = useQuery<ReadinessResponse>({
    queryKey: [`/api/crm/deals/${dealId}/readiness`],
    enabled: open && !!transition?.requiresReadinessCheck,
  });

  // Transition mutation
  const transitionMutation = useMutation({
    mutationFn: async () => {
      if (!transition) throw new Error("No transition selected");
      const response = await apiRequest(
        "POST",
        `/api/crm/deals/${dealId}/transition`,
        {
          targetTransactionStage: transition.targetStage,
          targetSubstage: transition.targetSubstage,
          reason: reason || undefined,
        }
      );
      return response.json();
    },
    onSuccess: () => {
      // Invalidate deal data to refresh stage indicator
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/deals", dealId],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/deals/${dealId}/valid-transitions`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/deals/${dealId}/stage-history`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/deals/${dealId}/activity`],
      });

      toast({
        title: "Deal stage updated",
        description: `Deal stage updated to ${transition?.label || "new stage"}`,
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Transition failed",
        description: error.message || "Failed to update deal stage",
        variant: "destructive",
      });
    },
  });

  if (!transition) return null;

  const requiresReadiness = transition.requiresReadinessCheck;
  const hasBlockers =
    requiresReadiness && readiness ? !readiness.canProceed : false;
  const isLoading = readinessLoading && requiresReadiness;
  const isTransitioning = transitionMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        // Prevent closing while transitioning
        if (isTransitioning) return;
        onOpenChange(newOpen);
      }}
    >
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="modal-stage-transition"
      >
        <DialogHeader>
          <DialogTitle data-testid="transition-modal-title">
            Transition: {transition.label}
          </DialogTitle>
          <DialogDescription>
            Confirm this stage transition for the deal.
          </DialogDescription>
        </DialogHeader>

        {/* Readiness Checks Section */}
        {requiresReadiness && (
          <div data-testid="readiness-checks-section" className="space-y-3">
            <h4 className="text-sm font-medium">Readiness Check Results</h4>

            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Running readiness checks...
                </span>
              </div>
            )}

            {readinessError && (
              <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Failed to load readiness checks. You may proceed at your own risk.
              </div>
            )}

            {readiness && (
              <div className="space-y-2">
                {readiness.checks.map((check, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-2 rounded-md border"
                    data-testid={`readiness-check-${index}`}
                  >
                    <SeverityIcon severity={check.severity} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant={severityBadgeVariant(check.severity)}
                          className={severityBadgeClassName(check.severity)}
                        >
                          {check.severity === "red"
                            ? "Blocker"
                            : check.severity === "amber"
                            ? "Warning"
                            : check.severity === "info"
                            ? "Info"
                            : "Passed"}
                        </Badge>
                        <span className="text-sm font-medium">
                          {check.checkName}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {check.message}
                        {check.value !== undefined && (
                          <span className="ml-1 font-medium">
                            ({String(check.value)})
                          </span>
                        )}
                      </p>
                      {check.resolution && (
                        <p className="text-xs text-muted-foreground mt-0.5 italic">
                          {check.resolution}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Blocker message */}
            {hasBlockers && readiness && (
              <div
                data-testid="blocker-message"
                className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm"
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  Resolve {readiness.blockerCount} blocking issue(s) before
                  proceeding
                </span>
              </div>
            )}
          </div>
        )}

        {/* No readiness required message */}
        {!requiresReadiness && (
          <div className="p-3 rounded-md bg-muted text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 inline mr-1 text-green-500" />
            No readiness checks required for this transition.
          </div>
        )}

        {/* Reason textarea */}
        <div className="space-y-2">
          <Label htmlFor="transition-reason">Reason (optional)</Label>
          <Textarea
            id="transition-reason"
            data-testid="input-transition-reason"
            placeholder="Enter a reason for this transition..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            disabled={isTransitioning}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isTransitioning}
            data-testid="button-cancel-transition"
          >
            Cancel
          </Button>
          <Button
            onClick={() => transitionMutation.mutate()}
            disabled={isTransitioning || isLoading || hasBlockers}
            data-testid="button-proceed-transition"
          >
            {isTransitioning && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {isTransitioning ? "Transitioning..." : "Proceed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
